import { Badge, Box, Flex, HStack, Text } from '@chakra-ui/react';
import type { Participant, MessageData } from '@fluenceur/types';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatShortTime } from '@/lib/format-short-time';
import { selectDeliveryStateKey } from './delivery-state-presentation';
import { RenderMessageFactory } from './messages';

interface MessageBubbleProps {
  message: MessageData;
  isSelf: boolean;
  participant?: Participant;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}

const MessageBubble = ({
  message,
  isSelf,
  participant,
  isFirstInGroup,
  isLastInGroup,
}: MessageBubbleProps) => {
  const { t, i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const isCreatorMessage = !isSelf;
  const getFormattedTime = (dateString: string) => formatShortTime(dateString, i18n?.language);

  const senderInfo = isSelf ? null : participant;
  const deliveryStateKey = selectDeliveryStateKey(message.deliveryState);

  // Determine group position
  let groupPosition: 'single' | 'first' | 'middle' | 'last';
  if (isFirstInGroup && isLastInGroup) {
    groupPosition = 'single';
  } else if (isFirstInGroup) {
    groupPosition = 'first';
  } else if (isLastInGroup) {
    groupPosition = 'last';
  } else {
    groupPosition = 'middle';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: reducedMotion ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reducedMotion ? 0 : 0.3,
        ease: [0.4, 0, 0.2, 1], // Custom easing for smooth acceleration
        delay: reducedMotion ? 0 : 0.1,
      }}
    >
      <Flex
        align={isCreatorMessage ? 'flex-end' : 'flex-start'}
        data-message-side={isCreatorMessage ? 'right' : 'left'}
        direction="column"
        mb={isLastInGroup ? 3 : 1}
        w="full"
      >
        {isFirstInGroup && senderInfo && (
          <HStack gap={2} align="center" justify="flex-end" mb={1} maxW="84%">
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {senderInfo.name}
            </Text>
            {message.channel && (
              <Badge colorPalette="gray" size="sm">
                {t(`messages.channel.${message.channel.type}`)}
              </Badge>
            )}
          </HStack>
        )}

        <Box maxW="84%">
          <RenderMessageFactory message={message} isSelf={isSelf} groupPosition={groupPosition} />
        </Box>
        {isLastInGroup && (
          <Text
            role={isSelf ? 'status' : undefined}
            aria-label={isSelf && deliveryStateKey ? t(deliveryStateKey) : undefined}
            color={message.deliveryState === 'failed' ? 'red.600' : 'gray.500'}
            fontSize="xs"
            fontWeight={message.deliveryState === 'failed' ? 'semibold' : 'normal'}
            mt={1}
            textAlign={isCreatorMessage ? 'right' : 'left'}
          >
            {isSelf && deliveryStateKey && `${t(deliveryStateKey)} · `}
            {getFormattedTime(message.sentAt)}
          </Text>
        )}
      </Flex>
    </motion.div>
  );
};

export default MessageBubble;
